<template>
  <div class="p-field">
    <div :class="{ 'p-field-horizontal': labelStyle === 'inline' }">
      <!-- Label -->
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- Input Wrapper -->

      <div class="p-input-wrapper">
        <div class="p-input-container">
          <DatePicker ref="datePickerRef" :id="id" v-model="localValue" :class="[
            'w-full',
            {
              'p-invalid': isInvalid,
              'p-disabled': disabled,
              [`p-variant-${variant}`]: variant,
              'direction-aware': true
            }
          ]" :size="size" :disabled="disabled" :placeholder="placeholder" :aria-label="ariaLabel"
            :aria-labelledby="ariaLabelledby" :aria-invalid="isInvalid" :aria-required="required"
            :aria-describedby="ariaDescribedBy" :required="required" :readonly="readonly" :showIcon="showIcon"
            :icon="icon" :dateFormat="dateFormat" :minDate="minDate" :maxDate="maxDate" :showTime="showTime"
            :timeOnly="timeOnly" :showSeconds="showSeconds" :hourFormat="hourFormat" :numberOfMonths="numberOfMonths"
            :view="view" :touchUI="effectiveTouchUI" :selectionMode="selectionMode" :inline="inline"
            :showButtonBar="showButtonBar" :showWeek="showWeek" :manualInput="manualInput" v-bind="$attrs"
            @date-select="onDateSelect" @show="onShow" @hide="onHide" @focus="onFocus" @blur="onBlur" @change="onChange"
            @month-change="onMonthChange" @year-change="onYearChange" @clear="onClear" @today-click="onTodayClick"
            @clear-click="onClearClick" />
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small v-for="(error, index) in localizedErrors" :key="index" :id="`${id}-error-${index}`" class="p-error block">
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { ONEDatepickerProps } from './types'

export default defineComponent({
  name: 'ONEDatepicker',
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import DatePicker from 'primevue/datepicker'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import { useResponsive } from '../../../../composables/useResponsive'

const props = withDefaults(defineProps<ONEDatepickerProps>(), {
  modelValue: null,
  size: undefined,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  placeholder: '',
  helperText: '',
  label: '',
  labelStyle: 'fixed',
  ariaLabel: '',
  ariaLabelledby: '',
  id: () => `one-datepicker-${Math.random().toString(36).substr(2, 9)}`,
  showIcon: true,
  icon: 'pi pi-calendar',
  dateFormat: 'dd M yy',
  minDate: undefined,
  maxDate: undefined,
  showTime: false,
  timeOnly: false,
  showSeconds: false,
  hourFormat: '24',
  variant: 'outlined',
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  numberOfMonths: 1,
  view: 'date',
  touchUI: false,
  selectionMode: 'single',
  inline: false,
  showButtonBar: false,
  showWeek: false,
  manualInput: true
})

type DatePickerEvent = {
  originalEvent: Event;
  value: Date | Date[] | null;
}

const emit = defineEmits<{
  'update:modelValue': [value: Date | Date[] | null]
  focus: [event: Event]
  blur: [event: DatePickerEvent]
  change: [event: DatePickerEvent]
  'date-select': [value: Date]
  show: []
  hide: []
  'validation-error': [errors: string[]]
  'validation-success': []
  'month-change': [event: { month: number; year: number }]
  'year-change': [event: { month: number; year: number }]
  clear: []
  'today-click': []
  'clear-click': []
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()
const { isMobile } = useResponsive()

// Auto-enable touchUI on mobile for better date picker UX
const effectiveTouchUI = computed(() => props.touchUI || isMobile.value)

// Local State
const datePickerRef = ref<any>(null)
const isFocused = ref(false)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const localValue = computed({
  get: () => props.modelValue,
  set: (newValue: Date | Date[] | null) => {
    emit('update:modelValue', newValue)
    if (formState && props.name) {
      formState.setFieldValue(props.name, newValue)
    }
  }
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    if (error.toLowerCase().includes('date')) {
      return t('validation.date')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onFocus = (event: Event) => {
  isFocused.value = true
  emit('focus', event)
}

const onBlur = (event: any) => {
  isFocused.value = false
  emit('blur', event as DatePickerEvent)
  isDirty.value = true
  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }
  if (props.validateOnBlur) {
    void validate()
  }
}

const onChange = (event: any) => {
  emit('change', event as DatePickerEvent)
  isDirty.value = true
  if (props.validateOnChange) {
    void validate()
  }
}

const onDateSelect = (value: Date) => {
  emit('date-select', value)
}

const onShow = () => {
  emit('show')
}

const onHide = () => {
  emit('hide')
}

const onMonthChange = (event: { month: number; year: number }) => {
  emit('month-change', event)
}

const onYearChange = (event: { month: number; year: number }) => {
  emit('year-change', event)
}

const onClear = () => {
  emit('clear')
}

const onTodayClick = () => {
  emit('today-click')
}

const onClearClick = () => {
  emit('clear-click')
}

const focus = () => {
  datePickerRef.value?.focus()
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

defineExpose({
  focus,
  blur: () => datePickerRef.value?.blur(),
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>
